import React, { useEffect, useState } from "react";

import { Icon, Swatch, cx } from "../catalogProductStudio/edit/StudioUi";
import {
  FEED_STATE_META,
  REACH_ISSUE_KEYS,
  STOCK_LEVEL_META,
  fill,
  formatPrice,
} from "./metaAdsModel";

// Presentation pieces for Marketing → Meta Ads. Styled in
// assets/css/marketing-meta-ads.css (`mka-` prefix) on top of the Product
// Studio tokens (`.pse`), so dark/light themes come for free.

/** Quiet status: a coloured dot and words, never a loud badge. */
export const FeedStatus = ({ stateKey, tt, title, className }) => {
  const meta = FEED_STATE_META[stateKey] || FEED_STATE_META.ready;
  return (
    <span className={cx("mka-status", `mka-status--${meta.tone}`, className)} title={title}>
      <span className="mka-status__dot" aria-hidden="true" />
      <span className="mka-status__label">{tt(meta.labelKey)}</span>
    </span>
  );
};

/** Image that degrades to a neutral placeholder (never a broken icon). */
export const ProductImage = ({ src, alt = "", className }) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  return (
    <span className={cx("mka-image", className)}>
      {src && !failed ? (
        <img src={src} alt={alt} loading="lazy" decoding="async" onError={() => setFailed(true)} />
      ) : (
        <span className="mka-image__fallback" aria-hidden="true">
          <Icon name="image" size={20} />
        </span>
      )}
    </span>
  );
};

/** Overlapping covers of a set's newest products. */
export const ThumbStack = ({ urls = [], count = 0 }) => {
  const shown = urls.slice(0, 3);
  if (!shown.length) {
    return (
      <span className="mka-stack mka-stack--empty" aria-hidden="true">
        <Icon name="layers" size={20} />
      </span>
    );
  }
  return (
    <span className="mka-stack" aria-hidden="true">
      {shown.map((url, index) => (
        <ProductImage key={`${url}-${index}`} src={url} className={`mka-stack__item mka-stack__item--${index}`} />
      ))}
      {count > shown.length ? <span className="mka-stack__more">+{count - shown.length}</span> : null}
    </span>
  );
};

export const PriceLine = ({ price }) => {
  if (!price || price.min === null || price.min === undefined) return null;
  const range = price.max !== null && price.max !== undefined && price.max > price.min;
  return (
    <span className={cx("mka-price", price.compareAt && "is-sale")}>
      <span className="mka-price__now">
        {formatPrice(price.min)}
        {range ? `–${formatPrice(price.max)}` : ""}
      </span>
      {price.compareAt ? <s className="mka-price__was">{formatPrice(price.compareAt)}</s> : null}
    </span>
  );
};

export const StockLine = ({ stock, tt }) => {
  const meta = STOCK_LEVEL_META[stock?.level] || STOCK_LEVEL_META.OK;
  const text =
    stock?.level === "OUT_OF_STOCK"
      ? tt(meta.labelKey)
      : fill(tt(stock?.level === "LOW_STOCK" ? "stockLowCount" : "stockInCount"), {
          count: stock?.available ?? 0,
        });
  return <span className={cx("mka-stock", `mka-stock--${meta.tone}`)}>{text}</span>;
};

/** One short reason the product will not show in Meta, or nothing. */
export const ReachNote = ({ product, tt }) => {
  const issue = product?.metaReach?.issues?.[0];
  if (!issue) return null;
  return (
    <span className="mka-reach" title={tt("reachHint")}>
      <Icon name="eyeOff" size={13} />
      <span>{tt(REACH_ISSUE_KEYS[issue])}</span>
    </span>
  );
};

/** "Black" with its swatch; nothing for models without colours. */
export const ColorLine = ({ product }) =>
  product.colorName ? (
    <span className="mka-color">
      <Swatch value={product.colorName} size={11} />
      <span className="mka-color__name">{product.colorName}</span>
    </span>
  ) : null;

/** Every size the selection covers, e.g. "Masat: 36, 37, 38". */
export const SizesLine = ({ product, tt }) => {
  if (!product.sizes?.length) return null;
  const sizes = product.sizes.join(", ");
  return (
    <span className="mka-sizes" title={sizes}>
      {fill(tt("sizesList"), { sizes })}
    </span>
  );
};

const productAriaLabel = (product, tt) =>
  [
    product.title,
    product.colorName,
    product.sizes?.length ? fill(tt("sizesList"), { sizes: product.sizes.join(", ") }) : null,
    product.sku,
    formatPrice(product.price?.min),
    product.metaReach?.issues?.[0] ? tt(REACH_ISSUE_KEYS[product.metaReach.issues[0]]) : null,
  ]
    .filter(Boolean)
    .join(", ");

/**
 * A selectable product. The whole tile is a <label> around a real checkbox,
 * so mouse, keyboard (Tab + Space) and screen readers all work the same way.
 */
export const ProductTile = ({ product, selected, onToggle, tt, layout = "grid" }) => {
  const handleChange = (event) => onToggle(product.key, event.nativeEvent?.shiftKey || false);
  const status = product.status !== "published" ? product.status : null;

  if (layout === "list") {
    return (
      <li className={cx("mka-row", selected && "is-selected")}>
        <label className="mka-row__label">
          <input
            type="checkbox"
            className="mka-check__input"
            checked={selected}
            onChange={handleChange}
            aria-label={productAriaLabel(product, tt)}
          />
          <span className="mka-check" aria-hidden="true">
            <Icon name="check" size={13} strokeWidth={3} />
          </span>
          <ProductImage src={product.imageUrl} className="mka-row__image" />
          <span className="mka-row__main">
            <span className="mka-row__title">
              {product.title}
              {product.colorName ? <span className="mka-row__color"> — {product.colorName}</span> : null}
            </span>
            <span className="mka-row__meta">
              {[
                product.sizes?.length ? fill(tt("sizesList"), { sizes: product.sizes.join(", ") }) : null,
                product.brandName,
                product.sku,
              ]
                .filter(Boolean)
                .join(" · ")}
            </span>
          </span>
          <span className="mka-row__price">
            <PriceLine price={product.price} />
          </span>
          <span className="mka-row__stock">
            <StockLine stock={product.stock} tt={tt} />
          </span>
          <span className="mka-row__notes">
            {status ? <span className="mka-tag">{tt(`status_${status}`)}</span> : null}
            <ReachNote product={product} tt={tt} />
          </span>
        </label>
      </li>
    );
  }

  return (
    <li className={cx("mka-tile", selected && "is-selected")}>
      <label className="mka-tile__label">
        <span className="mka-tile__media">
          <ProductImage src={product.imageUrl} className="mka-tile__image" />
          <input
            type="checkbox"
            className="mka-check__input"
            checked={selected}
            onChange={handleChange}
            aria-label={productAriaLabel(product, tt)}
          />
          <span className="mka-check mka-tile__check" aria-hidden="true">
            <Icon name="check" size={13} strokeWidth={3} />
          </span>
          {status ? <span className="mka-tag mka-tile__tag">{tt(`status_${status}`)}</span> : null}
          {product.price?.onSale ? <span className="mka-tile__sale">{tt("saleBadge")}</span> : null}
        </span>
        <span className="mka-tile__body">
          <span className="mka-tile__title" title={product.title}>
            {product.title}
          </span>
          <ColorLine product={product} />
          <SizesLine product={product} tt={tt} />
          <span className="mka-tile__meta">{[product.brandName, product.sku].filter(Boolean).join(" · ") || "—"}</span>
          <span className="mka-tile__facts">
            <PriceLine price={product.price} />
            <StockLine stock={product.stock} tt={tt} />
          </span>
          <ReachNote product={product} tt={tt} />
        </span>
      </label>
    </li>
  );
};

export const TileSkeletons = ({ count = 12, layout = "grid" }) => (
  <ul className={cx("mka-results", `mka-results--${layout}`)} aria-hidden="true">
    {Array.from({ length: count }).map((_, index) =>
      layout === "list" ? (
        <li key={index} className="mka-row mka-row--skeleton">
          <span className="mka-skel mka-skel--box" />
          <span className="mka-skel mka-skel--thumb" />
          <span className="mka-row__main">
            <span className="mka-skel mka-skel--line" />
            <span className="mka-skel mka-skel--line mka-skel--short" />
          </span>
        </li>
      ) : (
        <li key={index} className="mka-tile mka-tile--skeleton">
          <span className="mka-skel mka-skel--media" />
          <span className="mka-tile__body">
            <span className="mka-skel mka-skel--line" />
            <span className="mka-skel mka-skel--line mka-skel--short" />
          </span>
        </li>
      )
    )}
  </ul>
);

export const SetRowSkeletons = ({ count = 3 }) => (
  <ul className="mka-sets" aria-hidden="true">
    {Array.from({ length: count }).map((_, index) => (
      <li key={index} className="mka-set mka-set--skeleton">
        <span className="mka-skel mka-skel--stack" />
        <span className="mka-set__main">
          <span className="mka-skel mka-skel--line" />
          <span className="mka-skel mka-skel--line mka-skel--short" />
        </span>
        <span className="mka-skel mka-skel--pill" />
      </li>
    ))}
  </ul>
);
